/**
 * Friendly Error Messages
 * Converts technical errors into user-friendly messages for production
 * 
 * This uses both build-time (import.meta.env.DEV) and runtime checks (window location).
 * Error filtering is ALWAYS enabled when:
 * - Deployed to a non-localhost domain (production/staging)
 * - The app detects we're not in local development
 */

const isDevelopment = import.meta.env.DEV && (
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1'
);

interface ErrorPattern {
  pattern: RegExp | string;
  friendlyMessage: string;
}

const errorPatterns: ErrorPattern[] = [
  // Content Moderation - check this first before other patterns
  { pattern: /content.*policy|CONTENT_POLICY|moderation|flagged|inappropriate|prohibited|nsfw|safety.*block|blocked.*safety|Unprocessable/i, friendlyMessage: "This prompt was flagged by the model's content filter. Try rephrasing your prompt or using a different model — each model has different content guidelines." },
  
  // Network/Connection errors
  { pattern: /network|fetch|timeout|ECONNREFUSED|ERR_CONNECTION/i, friendlyMessage: "We're experiencing connectivity issues. Please check your internet connection and try again." },
  { pattern: /rate limit|too many requests|429/i, friendlyMessage: "We're experiencing high traffic right now. Please wait a moment and try again." },
  
  // Authentication/Authorization
  { pattern: /unauthorized|401|forbidden|403|authentication/i, friendlyMessage: "Your session has expired. Please sign in again." },
  
  // Server errors
  { pattern: /500|502|503|504|internal server|bad gateway|service unavailable/i, friendlyMessage: "Our servers are experiencing high demand. Please try again in a moment." },
  
  // Database/Storage
  { pattern: /database|storage|quota|disk space/i, friendlyMessage: "We're having trouble saving your work. Please try again." },
  
  // API specific
  { pattern: /openai|anthropic|gemini|api/i, friendlyMessage: "AI services are temporarily busy. Please try again in a moment." },
  
  // File/Upload errors
  { pattern: /upload|file size|too large/i, friendlyMessage: "File upload failed. Please check the file size and format, then try again." },
  
  // Invalid data
  { pattern: /invalid|malformed|parse|json/i, friendlyMessage: "Something went wrong processing your request. Please try again." },
  
  // Credit/Payment
  { pattern: /insufficient credits|no credits|balance/i, friendlyMessage: "You don't have enough credits for this action. Please add more credits to continue." },
];

/**
 * Returns true if the error is an insufficient credits error.
 */
export function isInsufficientCreditsError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return /insufficient credits|no credits/i.test(msg);
}

/**
 * Convert technical error to user-friendly message
 * In development, shows original error. In production, shows friendly message.
 */
export function getFriendlyErrorMessage(error: unknown, defaultMessage = "Something went wrong. Please try again."): string {
  // Extract message from any error shape (Error instance, plain object, or string)
  const errorMessage = error instanceof Error
    ? error.message
    : (error && typeof error === 'object' && 'message' in error)
      ? String((error as any).message)
      : typeof error === 'string'
        ? error
        : '';

  // Also extract error code if available (e.g. from FalAIError objects)
  const errorCode = (error && typeof error === 'object' && 'code' in error)
    ? String((error as any).code)
    : '';
  const searchString = `${errorMessage} ${errorCode}`;

  // In development, show original errors
  if (isDevelopment) {
    return errorMessage || defaultMessage;
  }
  
  // Check if error matches any known patterns (search both message and code)
  for (const { pattern, friendlyMessage } of errorPatterns) {
    if (typeof pattern === 'string') {
      if (searchString.toLowerCase().includes(pattern.toLowerCase())) {
        return friendlyMessage;
      }
    } else if (pattern.test(searchString)) {
      return friendlyMessage;
    }
  }
  
  // Default friendly message
  return defaultMessage;
}

/**
 * Specific friendly messages for common operations
 */
export const friendlyErrors = {
  // Load/Save operations
  loadFailed: "We couldn't load your content. Please refresh the page and try again.",
  saveFailed: "We're having trouble saving your work. Please try again in a moment.",
  
  // Generation operations
  generationFailed: "Generation is taking longer than expected due to high demand. Please try again.",
  imageGenerationFailed: "Image generation is temporarily unavailable. Please try again in a moment.",
  videoGenerationFailed: "Video generation is experiencing high demand. Please try again shortly.",
  contentModeration: "This prompt was flagged by the model's content filter. Try rephrasing your prompt or using a different model — each model has different content guidelines.",
  
  // Upload operations
  uploadFailed: "Upload failed. Please check your internet connection and try again.",
  
  // Import/Export
  importFailed: "We couldn't import your file. Please make sure it's a valid Knod file and try again.",
  exportFailed: "Export failed. Please try again.",
  
  // Template operations
  templateNotFound: "This template is no longer available.",
  templateLoadFailed: "We couldn't load this template. Please try another one.",
  
  // Generic operations
  operationFailed: "We're experiencing high traffic. Please try again in a moment.",
  networkError: "Please check your internet connection and try again.",
  serverBusy: "Our servers are experiencing high demand. Please try again shortly.",
} as const;
